import {translations} from 'presentation/translations-new';
import React, {Fragment} from 'react';

import {
	AlertModal,
	CTAButtonFixed,
	EmptySearchState,
	ErrorState,
	MainFooter,
	SentinelLoading,
	StyledGrid,
} from 'presentation/components';

import {Box, Button, Stack} from '@mui/material';
import {Authorization} from 'domain/usecases';
import {ModalError} from 'presentation/components/ModalError';
import Icon from 'presentation/icons';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {AutoSolEnum, FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {QueryParamsEnum} from 'presentation/constants';
import {AuthorizationsEmptyState} from 'presentation/components/AuthorizationsEmptyState';
import {Loading} from 'presentation/components/Loading/Loading';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {useGetPaginatedItemsReactQuery} from 'presentation/pages/private/hooks/useGetPaginatedItemsReactQuery';

import {
	AuthorizationCard,
	AuthorizationsFilter,
	ElectiveProceduresNotice,
} from '../../../components';
import {useAuthorizations} from '../../../hooks';
import {CustomCTAButtonFixedAction, StyledFooterContainer} from '../../../styles';
import {getAuthorizationsList} from '../../../services/getAuthorizationsList';
import {AuthorizationStatusEnum, LIST_SIZE} from '../../../constants';
import {ConfirmationDrawer} from '../../../components/ConfirmationDrawer';
import {useCongenereEligibilityStore} from 'presentation/pages/private/store/useCongenereEligibilityStore';

const {notice, tryAgain, agreementText, close} = translations['pt-br'].commons;
const {
	emptySearch,
	errorState,
	newRequest,
	scheduleErrorModal: {scheduleErrorTitle, scheduleErrorDescription},
} = translations['pt-br'].privateFlow.requestAuthorizations;
const {title} = translations['pt-br'].privateFlow.requestAuthorizations.businessHoursModal;
const {emptyFilteredDescription} = translations['pt-br'].commons;

export function AuthorizationsUnifiedList() {
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();
	const {isMobile, isTablet} = useMediaQueries();
	const congenereEligibility = useCongenereEligibilityStore(state => state.congenereEligibility);
	const {authorizationStatus} = SEARCH_PARAMS;

	const {
		handleGetAuthSchedule,
		modalDescriptionState,
		IS_LOADING,
		IS_ERROR,
		IS_DONE,
		IS_UNAVAILABLE,
		handleResetOperationState,
	} = useAuthorizations();

	const authorizationsPaginated = useGetPaginatedItemsReactQuery<Authorization>({
		queryKey: [QUERY_KEYS.AUTHORIZATIONS, LIST_SIZE],
		queryFn: ({page}) => getAuthorizationsList({page, size: LIST_SIZE}),
	});

	const hasStatusFilter =
		authorizationStatus && authorizationStatus !== AuthorizationStatusEnum.ALL;

	const filteredItems = hasStatusFilter
		? authorizationsPaginated.items.filter(item => item.dsSituacao === authorizationStatus)
		: authorizationsPaginated.items;

	const loadingItemsList = authorizationsPaginated.loadingItemsList;
	const itemsListHasError = authorizationsPaginated.itemsListHasError;
	const itemsListIsEmpty = authorizationsPaginated.itemsListIsEmpty;
	const hasNextPage = authorizationsPaginated.hasNextPage;

	function handleLoadMore() {
		authorizationsPaginated.loadMore();
	}

	function handleRefresh() {
		authorizationsPaginated.refresh();
	}

	function handleRemoveFilter() {
		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.AUTHORIZATION_STATUS);
			return prev;
		});
	}

	const currentSearchParams = new URLSearchParams(window.location.search);

	const shouldShowEmptyWithoutFilter =
		itemsListIsEmpty && !currentSearchParams.has(QueryParamsEnum.AUTHORIZATION_STATUS);

	const shouldShowEmptyWithFilter =
		filteredItems.length === 0 &&
		!itemsListIsEmpty &&
		!itemsListHasError &&
		currentSearchParams.has(QueryParamsEnum.AUTHORIZATION_STATUS) &&
		!loadingItemsList &&
		!hasNextPage;

	return (
		<Fragment>
			<Box>
				<Stack sx={{p: isMobile ? 2 : 4}} spacing={3}>
					<ElectiveProceduresNotice />

					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						gap={2}
						flexWrap="wrap">
						<AuthorizationsFilter />

						{!isTablet && !congenereEligibility ? (
							<Button
								onClick={handleGetAuthSchedule}
								size="large"
								disabled={IS_LOADING}
								endIcon={
									IS_LOADING ? (
										<Box width={24}>
											<Loading color="inherit" size={14} />
										</Box>
									) : (
										<Icon name="add-filled" />
									)
								}
								data-tag-id={AutoSolEnum.AS_CLICK_NOVA_SOLICITACAO_HISTORICO}>
								{newRequest}
							</Button>
						) : null}
					</Stack>

					<StyledGrid.Root>
						{filteredItems.map(item => (
							<StyledGrid.Item
								sm={12}
								md={12}
								lg={4}
								key={`${item.cdAutorizacaoSenha}-${item.cdProcedimento}-${item.nuProtocolo}`}>
								<AuthorizationCard {...item} />
							</StyledGrid.Item>
						))}
						<SentinelLoading
							minHeight="200px"
							onVisible={() => {
								if (authorizationsPaginated.items.length > 0) {
									handleLoadMore();
								}
							}}
							isLoading={loadingItemsList}
						/>
					</StyledGrid.Root>

					{shouldShowEmptyWithoutFilter ? (
						<EmptySearchState
							title={emptySearch.noAuthorizationsFound}
							description={emptySearch.noAuthorizationsFoundDescription}
							dataTagId={FinishWithoutCompleteFlowEnum.SEM_PROCEDIMENTO_ENCONTRADO}
						/>
					) : null}
					{shouldShowEmptyWithFilter ? (
						<AuthorizationsEmptyState
							description={emptyFilteredDescription}
							dataTagId={FinishWithoutCompleteFlowEnum.SEM_PROCEDIMENTO_ENCONTRADO}
							clearFilter={handleRemoveFilter}
						/>
					) : null}
					{itemsListHasError ? (
						<ErrorState
							title={errorState.title}
							description={errorState.description}
							handleRefresh={handleRefresh}
						/>
					) : null}
				</Stack>

				<AlertModal
					onClose={handleResetOperationState}
					open={IS_UNAVAILABLE}
					headerTitle={notice}
					title={title}
					description={modalDescriptionState}
					variant="info"
					buttonLabel={agreementText}
					data-tag-id={AutoSolEnum.AS_VISUALIZOU_MODAL_HORARIO}
					closeBtnDataTagId={AutoSolEnum.AS_CLICK_FECHAR_MODAL_HORARIO}
					buttonDataTagId={AutoSolEnum.AS_CLICK_BTN_ENTENDI}
				/>
				<ModalError
					onClose={handleResetOperationState}
					headerTitle={notice}
					description={scheduleErrorDescription}
					open={IS_ERROR}
					title={scheduleErrorTitle}
					firstButton={{
						title: tryAgain,
						handleClick: () => {
							handleResetOperationState();
							handleGetAuthSchedule();
						},
					}}
					secondButton={{
						title: close,
						handleClick: () => handleResetOperationState(),
					}}
				/>
			</Box>

			{isTablet && !congenereEligibility ? (
				<CTAButtonFixed.Root $isTablet>
					<CustomCTAButtonFixedAction
						fullWidth
						onClick={handleGetAuthSchedule}
						size="large"
						disabled={IS_LOADING}
						endIcon={
							IS_LOADING ? (
								<Box width={24}>
									<Loading color="inherit" size={14} />
								</Box>
							) : (
								<Icon name="add-filled" />
							)
						}
						data-tag-id={AutoSolEnum.AS_CLICK_NOVA_SOLICITACAO_HISTORICO}>
						{newRequest}
					</CustomCTAButtonFixedAction>
				</CTAButtonFixed.Root>
			) : null}

			<StyledFooterContainer $mb={108}>
				<MainFooter />
			</StyledFooterContainer>

			<ConfirmationDrawer opened={IS_DONE} handleClose={() => handleResetOperationState()} />
		</Fragment>
	);
}
